import { useState } from "react";
import { AlertTriangle, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { useAprobarCotizacion } from "@/hooks/useRevisiones";
import { getApiErrorMessage, parseValidationErrors } from "@/lib/api";
import type { Informe } from "@/lib/informes";

/**
 * Tope de correos en copia. Es el MISMO número que
 * `AprobarCotizacionRequest::MAXIMO_COPIAS` en el API: si se cambia uno hay que
 * cambiar el otro, o la pantalla deja agregar una copia que el servidor
 * rechaza con un 422 recién al enviar.
 */
export const MAXIMO_COPIAS = 5;

const correo = z.email("Escribe un correo válido.");

/**
 * Confirmación del envío de la cotización al cliente (RF-G3).
 *
 * POR QUÉ EXISTE: el botón de la pantalla de visado disparaba el envío al
 * primer clic, y lo que dispara no es un cambio de estado más. Sale un correo
 * FUERA de la organización, con el precio y el PDF adjunto, y el informe deja
 * de admitir una cotización nueva —`puedeCotizarse()` solo acepta `creado` y
 * `cotizacion_en_revision`—. O sea que el clic de más no se deshace corrigiendo:
 * se deshace hablando con el cliente.
 *
 * Por eso la modal ENUMERA las consecuencias antes de pedir nada, con el mismo
 * criterio que {@see ModalDeAceptacionManual}, y NOMBRA la casilla a la que va
 * el correo: el destinatario es una copia congelada del formulario que alguien
 * escribió semanas antes, y es justo el dato que conviene mirar dos veces antes
 * de mandarle un presupuesto a la dirección equivocada.
 *
 * LAS COPIAS SE VALIDAN AL AGREGARLAS, no al enviar. Una lista que se arma
 * escribiendo y se revisa al final devuelve el error cuando la persona ya se
 * comprometió con el botón; acá cada dirección entra a la lista solo si es una
 * dirección, y lo que se ve en pantalla es lo que va a salir.
 *
 * La validación de acá es una comodidad, no la garantía: el servidor valida las
 * mismas reglas, comprueba que a esta persona le toque visar y que la
 * cotización esté en un estado donde aprobar tenga sentido.
 */
export function ModalDeEnvioAlCliente({
  informe,
  abierto,
  onCerrar,
}: {
  informe: Informe;
  abierto: boolean;
  onCerrar: () => void;
}) {
  const aprobar = useAprobarCotizacion(informe.id);
  const [copias, setCopias] = useState<string[]>([]);
  const [borrador, setBorrador] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Pausada NO es en vuelo: sin red, TanStack deja la mutación `isPending` sin
  // haber enviado nada, y como el diálogo es modal y atrapa el foco, bloquear
  // el cierre solo por `isPending` encerraría al usuario hasta recuperar
  // conexión (mismo criterio que `ModalDeAceptacionManual`).
  const enVuelo = aprobar.isPending && !aprobar.isPaused;

  // Se normaliza el vacío a `null` porque el servidor decide con `blank()`: una
  // cadena de espacios NO le manda correo a nadie, y la pantalla que ofreciera
  // copias sobre ella prometería un envío que no ocurre.
  const destinatario = informe.email_contacto?.trim() || null;

  const cerrar = () => {
    // Se limpia al cerrar y no al abrir: si quedaran las copias de un intento
    // anterior, el siguiente informe arrancaría con los correos de otro cliente
    // ya cargados y a un clic de salir.
    setCopias([]);
    setBorrador("");
    setError(null);
    onCerrar();
  };

  const agregarCopia = () => {
    const valor = borrador.trim();

    if (valor === "") return;

    const validacion = correo.safeParse(valor);
    if (!validacion.success) {
      setError(validacion.error.issues[0].message);
      return;
    }

    // La misma comparación sin mayúsculas que hace el servidor al descartar
    // repetidas: SendGrid trata `Ana@x.cl` y `ana@x.cl` como la misma casilla.
    const normalizado = valor.toLowerCase();

    if (normalizado === destinatario?.toLowerCase()) {
      setError("Esa dirección ya es la del cliente: el correo le llega igual.");
      return;
    }

    if (copias.some((existente) => existente.toLowerCase() === normalizado)) {
      setError("Ese correo ya está en la lista.");
      return;
    }

    if (copias.length >= MAXIMO_COPIAS) {
      setError(`No se pueden agregar más de ${MAXIMO_COPIAS} correos en copia.`);
      return;
    }

    setCopias([...copias, valor]);
    setBorrador("");
    setError(null);
  };

  const enviar = () => {
    aprobar.mutate(copias, {
      onSuccess: () => {
        toast.success(
          destinatario
            ? "Cotización aprobada y enviada al cliente."
            : "Cotización aprobada. No se envió el correo: el informe no tiene correo de contacto.",
        );
        cerrar();
      },
      onError: (error) => {
        const errores = parseValidationErrors(error);

        // El 422 de las copias se pinta bajo el campo. Las claves llegan
        // indexadas (`cc.0`, `cc.1`) porque el servidor valida elemento por
        // elemento, así que se busca la primera que empiece por `cc`.
        const deLasCopias = errores
          ? Object.entries(errores).find(([campo]) => campo === "cc" || campo.startsWith("cc."))
          : undefined;

        if (deLasCopias) {
          setError(deLasCopias[1]);
          return;
        }

        toast.error(
          getApiErrorMessage(error, "No pudimos aprobar la cotización. Inténtalo de nuevo."),
        );
      },
    });
  };

  return (
    <Dialog
      open={abierto}
      onOpenChange={(estaAbierto) => {
        if (!estaAbierto && !enVuelo) cerrar();
      }}
    >
      <DialogContent
        className="sm:max-w-[34rem]"
        onEscapeKeyDown={(evento) => {
          if (enVuelo) evento.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Enviar cotización al cliente</DialogTitle>
          <DialogDescription>
            Revisa a quién le llega antes de aprobarla. Después de este paso, el precio ya está en
            manos del cliente.
          </DialogDescription>
        </DialogHeader>

        {/* Las consecuencias, antes de pedir nada. Una de ellas sale del
            sistema hacia el cliente y ninguna se deshace desde la pantalla. */}
        <div className="bg-muted/50 text-ink-muted rounded-lg border p-4 text-sm">
          <p className="text-ink mb-2 font-medium">Al aprobarla:</p>
          <ul className="list-disc space-y-1 pl-5">
            {destinatario ? (
              <li>
                Se le envía a <span className="text-ink font-mono">{destinatario}</span> el correo
                con el PDF de la cotización adjunto y el enlace para aceptarla en línea.
              </li>
            ) : (
              <li>
                No sale ningún correo al cliente:{" "}
                <span className="text-ink font-medium">este informe no tiene correo de contacto</span>.
              </li>
            )}
            <li>
              El informe pasa a <span className="text-ink font-medium">En espera de aceptación</span>{" "}
              y sale de la bandeja de revisiones.
            </li>
            <li>Al equipo del informe le llega el aviso de que la cotización quedó visada.</li>
            {/* Lo irreversible va último y dicho sin rodeos: `puedeCotizarse()`
                deja de aceptar el informe una vez enviado, así que "corregir el
                precio" deja de ser una opción de la pantalla. */}
            <li>
              Ya no se puede corregir el precio desde acá: el informe deja de admitir una cotización
              nueva.
            </li>
          </ul>
        </div>

        {destinatario === null && (
          <div className="bg-warning-soft text-warning-soft-foreground flex items-start gap-2.5 rounded-lg p-4 text-sm">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" />
            <p>
              El visado se registra igual, pero el cliente no se entera. Completa el correo de
              contacto en el informe si quieres que salga el presupuesto.
            </p>
          </div>
        )}

        {/* Las copias solo tienen sentido si hay un correo que copiar: van en
            la MISMA personalización que el destinatario, así que sin él no se
            manda nada a nadie. Ofrecer el campo prometería un envío que no
            ocurre. */}
        {destinatario !== null && (
          <div className="space-y-2">
            <Label htmlFor="copia-adicional">Enviar también en copia (opcional)</Label>

            <div className="flex items-start gap-2">
              <div className="flex-1 space-y-1.5">
                <Input
                  id="copia-adicional"
                  type="email"
                  inputMode="email"
                  autoComplete="off"
                  placeholder="contacto@empresa.cl"
                  value={borrador}
                  disabled={enVuelo || copias.length >= MAXIMO_COPIAS}
                  onChange={(evento) => {
                    setBorrador(evento.target.value);
                    setError(null);
                  }}
                  // Enter agrega la copia y NO envía el formulario: en un
                  // diálogo cuya acción manda un correo al cliente, la tecla
                  // que uno aprieta para "seguir escribiendo la lista" no
                  // puede ser la que despacha el presupuesto.
                  onKeyDown={(evento) => {
                    if (evento.key === "Enter" || evento.key === ",") {
                      evento.preventDefault();
                      agregarCopia();
                    }
                  }}
                  aria-invalid={error !== null}
                  aria-describedby={error ? "copia-adicional-error" : undefined}
                />
                {error ? (
                  <p id="copia-adicional-error" className="text-destructive text-xs">
                    {error}
                  </p>
                ) : (
                  <p className="text-ink-hint text-xs">
                    {copias.length >= MAXIMO_COPIAS
                      ? `Llegaste al máximo de ${MAXIMO_COPIAS} copias.`
                      : `Reciben el mismo correo, con el mismo PDF. Hasta ${MAXIMO_COPIAS}.`}
                  </p>
                )}
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={agregarCopia}
                disabled={enVuelo || borrador.trim() === "" || copias.length >= MAXIMO_COPIAS}
              >
                <Plus />
                Agregar
              </Button>
            </div>

            {copias.length > 0 && (
              <ul className="flex flex-wrap gap-2 pt-1">
                {copias.map((copia) => (
                  <li
                    key={copia}
                    className="bg-muted text-ink-body flex items-center gap-1.5 rounded-md py-1 pr-1 pl-2.5 text-xs"
                  >
                    <span className="font-mono">{copia}</span>
                    <button
                      type="button"
                      className="text-ink-hint hover:text-ink hover:bg-background rounded-sm p-0.5 transition-colors"
                      onClick={() => {
                        setCopias(copias.filter((otra) => otra !== copia));
                        setError(null);
                      }}
                      disabled={enVuelo}
                      aria-label={`Quitar ${copia} de la copia`}
                    >
                      <X className="size-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={cerrar} disabled={enVuelo}>
            Cancelar
          </Button>
          <Button type="button" onClick={enviar} disabled={enVuelo}>
            {enVuelo && <Spinner />}
            {enVuelo ? "Enviando…" : "Aprobar y enviar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
